import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { Manager, Reference, Popper } from 'react-popper'
import PropTypes from 'prop-types'
import Bars from 'assets/icons/bars.svg'
import Apps from 'assets/icons/apps.svg'
import Calendar from 'assets/icons/calendar.svg'
import Clock from 'assets/icons/clock.svg'
import ArrowUp from 'assets/icons/arrow-up.svg'
import ArrowRight from 'assets/icons/arrow-right.svg'
import ArrowDown from 'assets/icons/arrow-down.svg'
import ArrowLeft from 'assets/icons/arrow-left.svg'
import Check from 'assets/icons/check.svg'
import Cross from 'assets/icons/cross.svg'
import Warning from 'assets/icons/warning.svg'
import Info from 'assets/icons/info.svg'
import Chart from 'assets/icons/chart.svg'
import Download from 'assets/icons/download.svg'
import User from 'assets/icons/user.svg'
import Minus from 'assets/icons/minus.svg'
import Plus from 'assets/icons/plus.svg'
import Refresh from 'assets/icons/refresh.svg'
import Settings from 'assets/icons/settings.svg'
import Save from 'assets/icons/save.svg'
import Filter from 'assets/icons/filter.svg'
import Report from 'assets/icons/report.svg'
import LockOpen from 'assets/icons/lock-open.svg'
import LockClosed from 'assets/icons/lock-closed.svg'
import Expand from 'assets/icons/expand.svg'
import Retract from 'assets/icons/retract.svg'
import { withMargin, withWidth, withHeight } from 'utils/styled-decorators'

export const icons = {
  bars: Bars,
  apps: Apps,
  calendar: Calendar,
  clock: Clock,
  'arrow-up': ArrowUp,
  'arrow-right': ArrowRight,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  check: Check,
  cross: Cross,
  warning: Warning,
  info: Info,
  chart: Chart,
  download: Download,
  user: User,
  minus: Minus,
  plus: Plus,
  refresh: Refresh,
  settings: Settings,
  save: Save,
  filter: Filter,
  report: Report,
  'lock-open': LockOpen,
  'lock-closed': LockClosed,
  expand: Expand,
  retract: Retract
}

const StyledIcon = styled.div`
  background-color: ${({ theme, color }) => theme.colors[color] || theme.colors.gray};
  mask-image: url(${({ name }) => icons[name] || null });
  background-repeat: no-repeat;
  background-size: cover;
  mask-size: cover;
  min-width: ${({ width }) => width || '24px'};
  min-height: ${({ height }) => height || '24px'};
  ${({ onClick }) => onClick && 'cursor: pointer;'}
  ${withMargin()}
  ${withWidth('24px')}
  ${withHeight('24px')}
`

const Tooltip = styled.div`
  position: relative;
  font-family: 'Roboto';
  display: flex;
  visibility: ${({ open }) => open ? 'visible' : 'hidden'};
  opacity: ${({ open }) => open ? '1' : '0'};
  flex-direction: column;
  padding: 8px;
  margin: 8px;
  border-radius: 8px;
  z-index: 999;
  transition: all 0.2s ease;
  ${({ theme }) => css`
    background-color: ${theme.colors.darkGray};
    color: ${theme.colors.white};
    font-size: ${theme.fontSizes.small};
    box-shadow: ${theme.boxShadows.high};
  `}

  /* Arrow Style */
  div {
    content: '';
    transform: rotate(45deg);
    background: ${({ theme }) => theme.colors.darkGray };
    width: 10px;
    height: 10px;
    position: absolute;
    z-index: -1;
  }

  &[data-placement='bottom'] div {
    top: -4px;
  }
  &[data-placement='right'] div {
    left: -4px;
  }
  &[data-placement='left'] div {
    right: -4px;
  }
  &[data-placement='top'] div {
    bottom: -4px;
  }
`

const TooltipPopper = React.forwardRef(({ style, scheduleUpdate, tooltipPosition, placement, visibility, tooltip, arrowProps }, ref) => {
  useEffect(() => {
    scheduleUpdate()
  }, [visibility, scheduleUpdate])

  const tooltipStyle = {
    ...style,
    transform: `${style.transform} ${visibility ? 'translateX(0)' : 'scale(0.9)'}`
  }

  return <Tooltip open={visibility} tooltipPosition={tooltipPosition} ref={ref} style={tooltipStyle} data-placement={placement}>
    {tooltip}
    <div ref={arrowProps.ref} style={arrowProps.style} />
  </Tooltip>
})

const Icon = ({ tooltip, tooltipPosition, ...rest}) => {
  const [visibility, setVisibility] = useState(false)

  const showTooltip = () => setVisibility(true)
  const hideTooltip = () => setVisibility(false)

  const renderIcon = tooltip ? <Manager>
    <Reference>
      {({ ref }) => (
        <StyledIcon {...rest} ref={ref} onMouseEnter={showTooltip} onMouseLeave={hideTooltip} />
      )}
    </Reference>
    <Popper
      placement={tooltipPosition || 'top'}
      modifiers={{
        preventOverflow: {
          enabled: true,
          boundariesElement: 'viewport'
        }
      }}
    >
      {({ ref, style, placement, scheduleUpdate, arrowProps }) => <TooltipPopper {...{ style, scheduleUpdate, tooltipPosition, placement, visibility, tooltip, arrowProps, ref }} />}
    </Popper>
  </Manager> : <StyledIcon {...rest} />

  return renderIcon
}

Icon.propTypes = {
  /**
   * Define the icon that should be rendered
   */
  name: PropTypes.string,
  /**
   * Tooltip text that will appear when you hover the icon
   */
  tooltip: PropTypes.string,
  /**
   * Tooltip position (top | right | bottom | left)
   */
  tooltipPosition: PropTypes.string,
  /**
   * A color key defined in the theme
   */
  color: PropTypes.string,
  /**
   * Override CSS width property. Must be a valid CSS width value as a string
   */
  width: PropTypes.string,
  /**
   * Override CSS height property. Must be a valid CSS height value as a string
   */
  height: PropTypes.string,
  /**
   * Override CSS margin property. Must be a valid CSS margin value as a string
   */
  margin: PropTypes.string
}

export default Icon
